import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { wsManager } from "@/lib/websocket";
import { WS_CHANNELS, WS_EVENTS } from "@/lib/constants";
import { useAuth } from "@/hooks/use-auth";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/lib/constants";
import { toast } from "sonner";
import type {
  WSOrderUpdate,
  WSFraudAlert,
  WSSMSNotification,
  WSDeviceHeartbeat,
  WSDeviceOffline,
  WSRetroactiveMatch,
} from "@/types/api";

interface WebSocketContextValue {
  isConnected: boolean;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(
  undefined,
);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Handle order updates
  const handleOrderUpdate = useCallback(
    (data: WSOrderUpdate) => {
      console.log("Order update received:", data);

      // Invalidate orders query to refresh the list
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });

      // Show notification
      toast.info("Order Updated", {
        description: `Order ${data.orderId} status changed to ${data.status}`,
      });
    },
    [queryClient],
  );

  // Handle fraud alerts
  const handleFraudAlert = useCallback(
    (data: WSFraudAlert) => {
      console.log("Fraud alert received:", data);

      // Invalidate fraud queue to show new alert
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FRAUD_QUEUE });

      // Show critical notification
      toast.error("Fraud Alert", {
        description: `${data.riskLevel.toUpperCase()} risk detected for order ${data.orderId}`,
        duration: 10000, // Keep fraud alerts visible longer
      });
    },
    [queryClient],
  );

  // Handle SMS notifications
  const handleSMSNotification = useCallback((data: WSSMSNotification) => {
    console.log("SMS notification received:", data);

    toast.success("SMS Received", {
      description: `Transaction ${data.transactionId} - ৳${data.amount}`,
    });
  }, []);

  // Handle device heartbeat
  const handleDeviceHeartbeat = useCallback((data: WSDeviceHeartbeat) => {
    console.log("Device heartbeat:", data);
    // Optionally update device status in UI
  }, []);

  // Handle device offline
  const handleDeviceOffline = useCallback((data: WSDeviceOffline) => {
    console.log("Device offline:", data);

    toast.warning("Device Offline", {
      description: `Device ${data.deviceId} went offline`,
    });
  }, []);

  // Handle retroactive match
  const handleRetroactiveMatch = useCallback(
    (data: WSRetroactiveMatch) => {
      console.log("Retroactive match found:", data);

      // Invalidate orders to show the matched order
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });

      toast.info("Match Found", {
        description: `Order ${data.orderId} matched with SMS ${data.smsId}`,
      });
    },
    [queryClient],
  );

  // Handle connection status
  const handleConnected = useCallback(() => {
    setIsConnected(true);
    console.log("WebSocket connected successfully");
  }, []);

  const handleDisconnected = useCallback(() => {
    setIsConnected(false);
    console.log("WebSocket disconnected");
  }, []);

  const handleError = useCallback((data: { error: string }) => {
    console.error("WebSocket error:", data.error);
  }, []);

  // Subscribe to a channel
  const subscribe = useCallback((channel: string) => {
    wsManager.subscribe(channel);
  }, []);

  // Unsubscribe from a channel
  const unsubscribe = useCallback((channel: string) => {
    wsManager.unsubscribe(channel);
  }, []);

  // Setup WebSocket connection when user is authenticated
  useEffect(() => {
    if (!user) {
      wsManager.disconnect();
      return;
    }

    // Connect to WebSocket
    wsManager.connect();

    // Subscribe to all channels
    setTimeout(() => {
      wsManager.subscribe(WS_CHANNELS.ORDERS);
      wsManager.subscribe(WS_CHANNELS.FRAUD);
      wsManager.subscribe(WS_CHANNELS.SMS);
      wsManager.subscribe(WS_CHANNELS.DEVICES);
    }, 1000); // Small delay to ensure connection is established

    // Register event handlers
    wsManager.on("ws:connected", handleConnected);
    wsManager.on("ws:disconnected", handleDisconnected);
    wsManager.on("ws:error", handleError);
    wsManager.on(WS_EVENTS.ORDER_UPDATE, handleOrderUpdate);
    wsManager.on(WS_EVENTS.FRAUD_ALERT, handleFraudAlert);
    wsManager.on(WS_EVENTS.SMS_NOTIFICATION, handleSMSNotification);
    wsManager.on(WS_EVENTS.DEVICE_HEARTBEAT, handleDeviceHeartbeat);
    wsManager.on(WS_EVENTS.DEVICE_OFFLINE, handleDeviceOffline);
    wsManager.on(WS_EVENTS.RETROACTIVE_MATCH, handleRetroactiveMatch);

    // Cleanup on unmount
    return () => {
      wsManager.off("ws:connected", handleConnected);
      wsManager.off("ws:disconnected", handleDisconnected);
      wsManager.off("ws:error", handleError);
      wsManager.off(WS_EVENTS.ORDER_UPDATE, handleOrderUpdate);
      wsManager.off(WS_EVENTS.FRAUD_ALERT, handleFraudAlert);
      wsManager.off(WS_EVENTS.SMS_NOTIFICATION, handleSMSNotification);
      wsManager.off(WS_EVENTS.DEVICE_HEARTBEAT, handleDeviceHeartbeat);
      wsManager.off(WS_EVENTS.DEVICE_OFFLINE, handleDeviceOffline);
      wsManager.off(WS_EVENTS.RETROACTIVE_MATCH, handleRetroactiveMatch);

      wsManager.disconnect();
    };
  }, [
    user,
    handleConnected,
    handleDisconnected,
    handleError,
    handleOrderUpdate,
    handleFraudAlert,
    handleSMSNotification,
    handleDeviceHeartbeat,
    handleDeviceOffline,
    handleRetroactiveMatch,
  ]);

  const value: WebSocketContextValue = {
    isConnected,
    subscribe,
    unsubscribe,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
}
